<template>
  <div class="auth-form-container">
    <h2>Kirjaudu sisään</h2>
    <form @submit.prevent="signIn">
      <input v-model="username" placeholder="Käyttäjänimi tai sähköposti" required />
      <input v-model="password" placeholder="Salasana" type="password" required />
      <button type="submit" :disabled="isLoading">
        {{ isLoading ? 'Kirjaudutaan...' : 'Kirjaudu sisään' }}
      </button>
    </form>
    <div v-if="googleSignInAvailable" class="google-auth-section">
      <div class="google-auth-divider"><span>tai</span></div>
      <button
        type="button"
        class="google-sign-in-button"
        aria-describedby="google-sign-in-help"
        :disabled="isGoogleLoading"
        @click="signInWithGoogle"
      >
        <span class="google-mark" aria-hidden="true">G</span>
        {{ isGoogleLoading ? 'Siirrytään Googleen...' : 'Jatka Googlella' }}
      </button>
      <div id="google-sign-in-help" class="google-auth-note" role="note">
        <p>
          Googlella voit kirjautua tai luoda uuden tilin. Uutena käyttäjänä valitset seuraavaksi
          remmin nimen ja hyväksyt käyttöehdot – rekisteröitymissivulle ei tarvitse siirtyä erikseen.
        </p>
        <p>
          Jos vahvistettu Google-sähköpostisi vastaa olemassa olevaa Raviremmi-tiliä,
          Google-kirjautuminen liitetään siihen automaattisesti. Salasanakirjautuminen säilyy käytössä.
        </p>
        <p>
          Jos et halua yhdistää tilejä, käytä yllä käyttäjänimeä ja salasanaa. Raviremmi käyttää vain
          perustunnistautumistietoja eikä pyydä Googlelta Gmail- tai Drive-oikeuksia.
        </p>
      </div>
    </div>
    <div v-if="error" class="error-message">{{ error }}</div>
      <div class="auth-link">
        Eikö sinulla ole tiliä? <router-link to="/sign-up">Rekisteröidy</router-link>
      </div>
      <div class="auth-link forgot-password">
        <router-link to="/forgot-password">Unohditko salasanasi?</router-link>
      </div>
    </div>
</template>

<script>
import { ref } from 'vue';
import { useRouter } from 'vue-router';
import { Auth } from 'aws-amplify';

export default {
  name: 'SignIn',
  setup() {
    const username = ref('');
    const password = ref('');
    const error = ref('');
    const isLoading = ref(false);
    const isGoogleLoading = ref(false);
    const googleSignInAvailable = process.env.VUE_APP_GOOGLE_AUTH_ENABLED === 'true';
    const router = useRouter();

    const signIn = async () => {
      error.value = '';
      isLoading.value = true;
      try {
        const user = await Auth.signIn(username.value, password.value);
        console.log('Sign in successful:', user);

        // *** FIXED: Navigate to '/team' WITHOUT reloading the page ***
        // Vue Router will handle the navigation, and App.vue's state
        // (ideally updated via Amplify Hub listener) will update the UI.
        router.push('/team');

        // *** REMOVED: window.location.reload(); ***
        // This was causing the issue by interrupting the navigation.

      } catch (err) {
        // Translate common error messages
        if (err.code === 'UserNotConfirmedException') {
          error.value = 'Tiliä ei ole vahvistettu. Tarkista sähköpostisi tai vahvista tilisi.';
          // Keep redirect to confirm sign up page if user is not confirmed
          router.push({ path: '/confirm-sign-up', query: { username: username.value } });
        } else if (err.code === 'NotAuthorizedException') {
          error.value = 'Virheellinen käyttäjänimi tai salasana.';
        } else if (err.code === 'UserNotFoundException') {
          error.value = 'Käyttäjää ei löydy.';
        } else {
          error.value = err.message || 'Kirjautumisen aikana tapahtui virhe.';
        }
        console.error('Sign in error:', err);
      } finally {
        isLoading.value = false;
      }
    };

    const signInWithGoogle = async () => {
      error.value = '';
      isGoogleLoading.value = true;
      try {
        await Auth.federatedSignIn({ provider: 'Google' });
      } catch (err) {
        console.error('Google sign-in error:', err);
        error.value = err.message || 'Google-kirjautumisen aloittaminen epäonnistui.';
        isGoogleLoading.value = false;
      }
    };

    return {
      username,
      password,
      error,
      isLoading,
      isGoogleLoading,
      googleSignInAvailable,
      signIn,
      signInWithGoogle,
    };
  },
};
</script>

<style scoped>
/* Styles for the form container */
.auth-form-container {
  max-width: 450px;
  margin: 30px auto;
  padding: 30px 40px;
  background-color: #112240;
  border-radius: 8px;
  box-shadow: 0 10px 30px -10px rgba(2, 12, 27, 0.7);
  border: 1px solid #1e3a5f;
}

/* Heading style */
.auth-form-container h2 {
  color: #ffffff;
  text-align: center;
  margin-bottom: 25px;
  font-size: 1.8rem;
  font-weight: 500;
}

/* Form layout */
.auth-form-container form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

/* --- CORRECTED INPUT STYLES --- */
/* Target common text/password inputs AND inputs without a type (defaulting to text) */
.auth-form-container input[type="text"],
.auth-form-container input[type="email"],
.auth-form-container input[type="password"],
.auth-form-container input:not([type]) { /* Added this selector */
  background-color: #0a192f;
  border: 1px solid #1e3a5f;
  color: #ccd6f6;
  padding: 12px 15px;
  border-radius: 4px;
  font-size: 1rem;
  transition: border-color 0.3s ease, box-shadow 0.3s ease;
}

/* --- CORRECTED INPUT FOCUS STYLES --- */
.auth-form-container input[type="text"]:focus,
.auth-form-container input[type="email"]:focus,
.auth-form-container input[type="password"]:focus,
.auth-form-container input:not([type]):focus { /* Added this selector */
  outline: none;
  border-color: #64ffda; /* Teal border */
  box-shadow: 0 0 0 3px rgba(100, 255, 218, 0.15); /* Teal glow */
}

/* Placeholder text style (applies to all inputs) */
.auth-form-container input::placeholder {
  color: #8892b0;
  opacity: 0.7;
}

/* Submit button style */
.auth-form-container button[type="submit"] {
  background-color: #64ffda;
  color: #0a192f;
  border: none;
  padding: 12px 20px;
  font-size: 1rem;
  font-weight: 600;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.3s ease, transform 0.2s ease, opacity 0.3s ease;
  margin-top: 10px;
}

.auth-form-container button[type="submit"]:hover:not(:disabled) {
  background-color: #52d6c0;
  transform: translateY(-1px);
}

.auth-form-container button[type="submit"]:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Error message style */
.error-message {
  color: #ff6b6b;
  background-color: rgba(255, 107, 107, 0.1);
  border: 1px solid rgba(255, 107, 107, 0.3);
  padding: 10px 15px;
  border-radius: 4px;
  font-size: 0.9rem;
  text-align: center;
  margin-top: 20px;
}

/* Link style */
.auth-link {
    text-align: center;
    margin-top: 25px;
    font-size: 0.9rem;
    color: #a8b2d1;
}

.auth-link.forgot-password {
    margin-top: 15px;
    font-size: 0.85rem;
}

.auth-link a {
    color: #64ffda;
    text-decoration: none;
    font-weight: 500;
    transition: color 0.3s ease;
}

.auth-link a:hover {
  color: #ffffff;
  text-decoration: underline;
}

.google-auth-section {
  margin-top: 22px;
}

.google-auth-divider {
  display: flex;
  align-items: center;
  gap: 10px;
  color: #8892b0;
  font-size: 0.82rem;
  margin-bottom: 14px;
}

.google-auth-divider::before,
.google-auth-divider::after {
  content: '';
  flex: 1;
  height: 1px;
  background: #1e3a5f;
}

.google-sign-in-button {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  background: #f8fafc;
  color: #1f2937;
  border: 1px solid #cbd5e1;
  border-radius: 4px;
  padding: 11px 16px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
}

.google-sign-in-button:hover:not(:disabled) {
  background: #ffffff;
  border-color: #94a3b8;
}

.google-sign-in-button:disabled {
  opacity: 0.65;
  cursor: wait;
}

.google-mark {
  font-family: Georgia, serif;
  font-size: 1.2rem;
  font-weight: 700;
  color: #4285f4;
}

.google-auth-note {
  color: #8892b0;
  font-size: 0.78rem;
  line-height: 1.45;
  margin: 11px 2px 0;
  text-align: left;
}

.google-auth-note p {
  margin: 0;
}

.google-auth-note p + p {
  margin-top: 8px;
}
</style>
